/**
 * Behavioral Score Card Component
 *
 * Displays an entity's behavioral profile including:
 * - Growth Index / Risk Index scores
 * - 6-dimension radar chart
 * - Valence distribution
 * - Top behavioral codes
 * - Time window selector
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Alert
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import RefreshIcon from '@mui/icons-material/Refresh';
import IconButton from '@mui/material/IconButton';
import { RadarChartWithLegend } from '../Charts/RadarChart';
import { VALENCE_COLORS } from '../../constants/workbenchConfig';
import * as codeService from '../../services/behavioralCodeService';

/**
 * Score display with color coding
 */
function ScoreDisplay({ label, value, icon: Icon, color, description }) {
  return (
    <Box sx={{ textAlign: 'center', flex: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
        {Icon && <Icon sx={{ fontSize: 16, color }} />}
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      </Box>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          color,
          lineHeight: 1.2,
        }}
      >
        {value}
      </Typography>
      {description && (
        <Typography variant="caption" color="text.secondary">
          {description}
        </Typography>
      )}
    </Box>
  );
}

/**
 * Valence bar component
 */
function ValenceBar({ positive, negative, neutral, total }) {
  if (total === 0) return null;

  return (
    <Box sx={{ mt: 1 }}>
      <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          Valence Distribution ({total} codes)
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          height: 8,
          borderRadius: 4,
          overflow: 'hidden',
          bgcolor: 'rgba(0,0,0,0.08)',
        }}
      >
        {positive > 0 && (
          <Tooltip title={`Positive: ${Math.round(positive * 100)}%`}>
            <Box
              sx={{
                width: `${positive * 100}%`,
                bgcolor: VALENCE_COLORS.positive,
                transition: 'width 0.3s ease',
              }}
            />
          </Tooltip>
        )}
        {neutral > 0 && (
          <Tooltip title={`Neutral: ${Math.round(neutral * 100)}%`}>
            <Box
              sx={{
                width: `${neutral * 100}%`,
                bgcolor: VALENCE_COLORS.neutral,
                transition: 'width 0.3s ease',
              }}
            />
          </Tooltip>
        )}
        {negative > 0 && (
          <Tooltip title={`Negative: ${Math.round(negative * 100)}%`}>
            <Box
              sx={{
                width: `${negative * 100}%`,
                bgcolor: VALENCE_COLORS.negative,
                transition: 'width 0.3s ease',
              }}
            />
          </Tooltip>
        )}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
        <Typography variant="caption" sx={{ color: VALENCE_COLORS.positive }}>
          +{Math.round(positive * 100)}%
        </Typography>
        <Typography variant="caption" sx={{ color: VALENCE_COLORS.neutral }}>
          ~{Math.round(neutral * 100)}%
        </Typography>
        <Typography variant="caption" sx={{ color: VALENCE_COLORS.negative }}>
          -{Math.round(negative * 100)}%
        </Typography>
      </Box>
    </Box>
  );
}

/**
 * Top codes list
 */
function TopCodesList({ codes, limit = 5 }) {
  if (!codes || Object.keys(codes).length === 0) return null;

  const sortedCodes = Object.entries(codes)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit);

  const maxCount = sortedCodes[0]?.[1] || 1;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Top Behavioral Codes
      </Typography>
      {sortedCodes.map(([code, count]) => (
        <Box key={code} sx={{ mb: 0.75 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
            <Typography variant="caption" sx={{ fontWeight: 500 }}>
              {code.replace(/_/g, ' ')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {count}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={(count / maxCount) * 100}
            sx={{
              height: 4,
              borderRadius: 2,
              bgcolor: 'rgba(0,0,0,0.08)',
              '& .MuiLinearProgress-bar': {
                bgcolor: 'accent.teal',
                borderRadius: 2,
              },
            }}
          />
        </Box>
      ))}
    </Box>
  );
}

/**
 * Main Behavioral Score Card Component
 */
// eslint-disable-next-line max-lines-per-function
function BehavioralScoreCard({ entityId, entityName }) {
  const [scores, setScores] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeWindow, setTimeWindow] = useState(90);

  // Fetch scores
  const fetchScores = useCallback(async () => {
    if (!entityId) {
      setScores(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const result = await codeService.getEntityScores(entityId, {
        timeWindow,
        includeDimensions: true,
        includeHistory: false,
        includeCodeDistribution: true,
      });

      if (result.success) {
        setScores(result);
      } else {
        setError(result.message || 'Failed to load scores');
      }
    } catch (err) {
      console.error('Failed to fetch entity scores:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [entityId, timeWindow]);

  // Load scores on mount and when entity/time changes
  useEffect(() => {
    fetchScores();
  }, [fetchScores]);

  // Handle time window change
  const handleTimeWindowChange = (event, newValue) => {
    if (newValue !== null) {
      setTimeWindow(newValue);
    }
  };

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  // No entity selected
  if (!entityId) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        Select an entity to view behavioral scores.
      </Typography>
    );
  }

  // No scores available
  if (!scores || scores.codeApplicationCount === 0) {
    return (
      <Box sx={{ py: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No behavioral codes have been applied yet.
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Start coding document chunks to build the profile.
        </Typography>
      </Box>
    );
  }

  const { growthIndex, riskIndex } = scores.scores || {};
  const growthColor = growthIndex >= 60 ? '#23A38F' : growthIndex >= 40 ? '#D8A200' : '#FF3749';
  const riskColor = riskIndex <= 40 ? '#23A38F' : riskIndex <= 60 ? '#D8A200' : '#FF3749';

  return (
    <Box>
      {/* Header with time window selector */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {entityName || 'Entity Profile'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <ToggleButtonGroup
            value={timeWindow}
            exclusive
            onChange={handleTimeWindowChange}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                py: 0.25,
                px: 1,
                fontSize: '0.65rem',
              },
            }}
          >
            <ToggleButton value={30}>30d</ToggleButton>
            <ToggleButton value={60}>60d</ToggleButton>
            <ToggleButton value={90}>90d</ToggleButton>
            <ToggleButton value={120}>120d</ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Refresh scores">
            <IconButton size="small" onClick={fetchScores}>
              <RefreshIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Score Cards */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          bgcolor: 'rgba(0,0,0,0.02)',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', gap: 2 }}>
          <ScoreDisplay
            label="Growth Index"
            value={growthIndex || 0}
            icon={TrendingUpIcon}
            color={growthColor}
          />
          <Box sx={{ width: '1px', bgcolor: 'divider' }} />
          <ScoreDisplay
            label="Risk Index"
            value={riskIndex || 0}
            icon={TrendingDownIcon}
            color={riskColor}
          />
        </Box>

        {/* Valence Distribution */}
        {scores.valenceRatios && (
          <ValenceBar
            positive={scores.valenceRatios.positive}
            negative={scores.valenceRatios.negative}
            neutral={scores.valenceRatios.neutral}
            total={scores.valenceRatios.total}
          />
        )}
      </Paper>

      {/* Radar Chart */}
      {scores.dimensions && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            bgcolor: 'rgba(0,0,0,0.02)',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <RadarChartWithLegend
            dimensions={scores.dimensions}
            size={180}
            showLegend={false}
            title="Dimension Scores"
          />
        </Paper>
      )}

      {/* Top Codes */}
      {scores.frequencyDistribution && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            bgcolor: 'rgba(0,0,0,0.02)',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <TopCodesList codes={scores.frequencyDistribution} limit={5} />
        </Paper>
      )}

      {/* Stats footer */}
      <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center', gap: 2 }}>
        <Typography variant="caption" color="text.secondary">
          {scores.chunkCount} chunks
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {scores.codeApplicationCount} codes applied
        </Typography>
      </Box>
    </Box>
  );
}

export default BehavioralScoreCard;
